import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { Flex, Title, toast } from '@kognitos/lattice';
import { getRoutePath } from '@/routes/util';
import {
  V1AutomationStage,
  type V1ListAutomationRevisionSummariesResponse,
} from '@/generated/hooks/grimoire/models';
import {
  listAutomationRevisionSummaries,
  getListAutomationRevisionSummariesQueryKey,
  useRestoreAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import { captureException } from '@/modules/sentry';
import { errorHandler } from '@/shared/utils/error-handler';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import { VersionHistoryStageFilter } from './VersionHistoryStageFilter';
import { VersionHistoryList } from './VersionHistoryList';
import {
  EVersionHistoryMenuItemKey,
  type IVersionHistoryItemData,
  type IVersionHistoryMenuLabels,
} from './types';
import type { IDiffChangeSummary } from '@/shared/text-diffs/utils/diff-types';
import { RestoreAlertDialog } from './RestoreAlertDialog';
import {
  transformRevisionsToListItems,
  getStageFilterExpression,
} from './utils';
import { AutomationOpenDraftDialog } from '@/modules/automation/components/AutomationOpenDraftDialog';
import { useBoolean } from 'usehooks-ts';

const PAGE_SIZE = 10;

/**
 * Props for the VersionHistoryPanel component.
 */
export interface IVersionHistoryPanelProps {
  automationId: string;
  /** Whether viewing from a published automation context */
  fromPublished?: boolean;
  /** Initial filter value - controlled by parent */
  initialFilter: V1AutomationStage;
  /**
   * Fires when a version is selected. `previous` is the version immediately
   * before it (older) so the parent can diff "what this version changed" —
   * undefined for the oldest version (nothing precedes it).
   */
  onVersionSelect?: (
    version: IVersionHistoryItemData,
    previous?: IVersionHistoryItemData,
  ) => void;
  /** Callback when filter changes, used to control diff mode in parent */
  onFilterChange?: (filter: V1AutomationStage) => void;
  /** Fires when a filter change invalidates the selected pair, so the parent stops diffing it. */
  onSelectionClear?: () => void;
  /** Callback when restore is successful, used to close the panel */
  onRestoreSuccess?: () => void;
  /**
   * Which version to auto-select once the list loads (only applies when
   * nothing is selected yet). `'latest'` (default) selects the most recent
   * version. `'previous'` selects the version right before it — used for
   * "See what changed" after a quill build, to land directly on a diff
   * between the last preview revision and the just-built one.
   */
  initialSelectionMode?: 'latest' | 'previous';
  /**
   * Changes the selected version introduced vs latest (computed by the parent
   * from the same diff the document renders). Rendered as sub-items beneath
   * the selected version. Undefined when no diff is active.
   */
  changes?: IDiffChangeSummary[];
  /** Hover a change sub-item / leave (null) to preview it in the doc. */
  onChangeActivate?: (change: IDiffChangeSummary | null) => void;
  /** Click a change sub-item to scroll the document to it. */
  onChangeSelect?: (change: IDiffChangeSummary) => void;
}

/**
 * Panel component that displays version history with a title and filter dropdown.
 *
 * Fetches automation revisions from the API, transforms them to an array of versions, and passes them to VersionHistoryList.
 * Provides filtering capabilities to show all versions, only published, or only drafts.
 * Automatically selects the most recent version on initial load.
 *
 * @param props - Component props
 * @returns Version history panel component
 *
 * @example
 * <VersionHistoryPanel
 *   automationId="automation-123"
 *   onVersionSelect={handleVersionSelect}
 * />
 */
export function VersionHistoryPanel({
  automationId,
  fromPublished,
  initialFilter,
  onVersionSelect,
  onFilterChange,
  onSelectionClear,
  onRestoreSuccess,
  initialSelectionMode = 'latest',
  changes,
  onChangeActivate,
  onChangeSelect,
}: IVersionHistoryPanelProps) {
  const { t } = useTranslation('automation');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    value: isDraftDialogOpen,
    setTrue: openDraftDialog,
    setFalse: closeDraftDialog,
  } = useBoolean(false);

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const [filter, setFilter] = useState<V1AutomationStage>(initialFilter);

  // Internal state to track the selected version ID
  const [selectedVersionId, setSelectedVersionId] = useState<
    string | undefined
  >(undefined);

  // Set by a filter change that keeps the selection: the retained version's
  // predecessor has to be re-derived from the newly scoped list.
  const [needsPairRederive, setNeedsPairRederive] = useState(false);

  const {
    value: isRestoreDialogOpen,
    setTrue: openRestoreDialog,
    setFalse: closeRestoreDialog,
  } = useBoolean(false);

  // Track which version was clicked for restore (separate from selected version)
  const [versionToRestore, setVersionToRestore] =
    useState<IVersionHistoryItemData | null>(null);

  // Mutation for restoring automation to a previous version
  const { mutate: restoreAutomation, isPending: isRestoring } =
    useRestoreAutomation();

  // Convert filter state to CEL expression for API
  const filterExpression = getStageFilterExpression(filter);

  /**
   * Refetches the revision summaries after an action that creates a new
   * revision. This panel and the export/move version pickers all read the same
   * summaries endpoint, so one invalidation covers every consumer.
   */
  const invalidateRevisionLists = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getListAutomationRevisionSummariesQueryKey(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
      ),
    });
  }, [queryClient, currentOrganizationId, currentWorkspaceId, automationId]);

  // Fetch revision summaries from API with pagination and server-side
  // filtering. Summaries blank out each snapshot's content fields (`code`,
  // `english_code`, `description`), which keeps the response under the API
  // gateway's gRPC-JSON transcoding limit — the full-revision list endpoint
  // returns multi-MB pages and 500s. Nothing here reads those fields: content
  // for the selected revision is fetched separately via GetAutomationRevision.
  const {
    data: revisionsData,
    isLoading,
    isFetching,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<
    AxiosResponse<V1ListAutomationRevisionSummariesResponse>,
    Error,
    InfiniteData<
      AxiosResponse<V1ListAutomationRevisionSummariesResponse>,
      string | undefined
    >,
    ReturnType<typeof getListAutomationRevisionSummariesQueryKey>,
    string | undefined
  >({
    queryKey: getListAutomationRevisionSummariesQueryKey(
      currentOrganizationId || '',
      currentWorkspaceId || '',
      automationId,
      { page_size: PAGE_SIZE, filter: filterExpression },
    ),
    queryFn: ({ pageParam, signal }) =>
      listAutomationRevisionSummaries(
        currentOrganizationId || '',
        currentWorkspaceId || '',
        automationId,
        {
          page_size: PAGE_SIZE,
          page_token: pageParam,
          filter: filterExpression,
        },
        { signal },
      ),
    enabled: Boolean(
      automationId && currentOrganizationId && currentWorkspaceId,
    ),
    initialPageParam: undefined,
    staleTime: 60000,
    gcTime: 60000,
    getNextPageParam: lastPage => lastPage.data.next_page_token || undefined,
  });

  /**
   * Handles menu item click.
   * @param key - The key of the menu item clicked
   * @param versionData - The version data for the item these menu items belong to
   */
  const handleMenuItemClick = useCallback(
    (key: string, versionData: IVersionHistoryItemData) => {
      switch (key) {
        case EVersionHistoryMenuItemKey.CREATE_DRAFT:
          openDraftDialog();
          break;
        case EVersionHistoryMenuItemKey.RESTORE:
          setVersionToRestore(versionData);
          openRestoreDialog();
          break;
        default:
          break;
      }
    },
    [openDraftDialog, openRestoreDialog],
  );

  const menuLabels: IVersionHistoryMenuLabels = useMemo(
    () => ({
      createDraft: t('automation.versionHistory.menu.createDraft'),
      restore: t('automation.versionHistory.menu.restore'),
    }),
    [t],
  );

  // Flatten paginated revisions into a single array
  const revisions = useMemo(() => {
    return (
      revisionsData?.pages.flatMap(
        (page: AxiosResponse<V1ListAutomationRevisionSummariesResponse>) =>
          page.data.automation_revision_summaries ?? [],
      ) ?? []
    );
  }, [revisionsData]);

  // Transform API response to list items (server handles filtering)
  const versions = useMemo(() => {
    if (revisions.length === 0) {
      return [];
    }

    return transformRevisionsToListItems(
      revisions,
      menuLabels,
      handleMenuItemClick,
    );
  }, [revisions, menuLabels, handleMenuItemClick]);

  // Ref to store latest onVersionSelect to keep selectVersion stable
  const onVersionSelectRef = useRef(onVersionSelect);
  onVersionSelectRef.current = onVersionSelect;

  // Ref to the current (flattened, newest-first) versions so selectVersion can
  // look up a selected version's predecessor without re-creating the callback.
  const versionsRef = useRef(versions);
  versionsRef.current = versions;

  /**
   * Selects a version - updates internal state and notifies parent with the
   * version's predecessor (the next, older entry — the list is newest-first)
   * so the parent can diff "what changed in this version". Uses refs to avoid
   * re-creating the callback when props change.
   */
  const selectVersion = useCallback((version: IVersionHistoryItemData) => {
    setSelectedVersionId(version.version);
    const all = versionsRef.current;
    const idx = all.findIndex(v => v.version === version.version);
    const previous = idx >= 0 ? all[idx + 1] : undefined;
    onVersionSelectRef.current?.(version, previous);
  }, []);

  /**
   * Handles filter selection change.
   * When switching TO "All versions", keep the current selection.
   * When switching FROM "All versions" to filtered view, clear selection for auto-select.
   * Notifies parent of filter change for diff mode control.
   */
  const handleFilterChange = (value: V1AutomationStage) => {
    const isChangingToAllVersions =
      value === V1AutomationStage.AUTOMATION_STAGE_UNSPECIFIED;

    setFilter(value);

    // Either way the parent's pair is stale: re-scoping the list re-scopes the
    // selected version's predecessor with it.
    onSelectionClear?.();

    if (isChangingToAllVersions) {
      setNeedsPairRederive(true);
    } else {
      setSelectedVersionId(undefined);
    }

    onFilterChange?.(value);
  };

  /**
   * Handles restore dialog close - resets dialog state.
   */
  const handleRestoreDialogClose = useCallback(() => {
    closeRestoreDialog();
    setVersionToRestore(null);
  }, [closeRestoreDialog]);

  /**
   * Handles restoring an automation to a previous version.
   * Calls the RestoreAutomation API to create a new draft version from the selected version.
   */
  const handleRestoreConfirm = useCallback(() => {
    if (!versionToRestore || !currentOrganizationId || !currentWorkspaceId) {
      return;
    }

    // Revision number without "v" prefix
    const { revisionId } = versionToRestore;

    restoreAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        data: {
          revision_id: revisionId,
        },
      },
      {
        onSuccess: () => {
          // Refetch the updated list with the new draft
          invalidateRevisionLists();
          toast.success(t('automation.versionHistory.restore.success'));
          handleRestoreDialogClose();

          // Notify parent to close the panel (when restoring from draft editor view)
          onRestoreSuccess?.();

          // Navigate to the draft editor with the restored draft
          navigate(
            getRoutePath('DRAFT_EDITOR', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              draftId: automationId, // the draftId is the same as the automationId for a restored draft
            }),
          );
        },
        onError: error => {
          errorHandler(error, () => {
            captureException(
              error instanceof Error ? error : new Error(String(error)),
              {
                tags: {
                  component: 'VersionHistoryPanel',
                  action: 'restoreAutomation',
                },
                extra: {
                  automationId,
                  revisionId,
                },
              },
            );
            toast.error(t('automation.versionHistory.restore.error'));
          });
        },
      },
    );
  }, [
    versionToRestore,
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    restoreAutomation,
    invalidateRevisionLists,
    t,
    handleRestoreDialogClose,
    onRestoreSuccess,
    navigate,
  ]);

  // Show loading on initial load, when fetching with no data (e.g., filter change with empty cache),
  // or when selection was just cleared during a fetch (filter change transition to prevent flicker)
  const showLoading =
    isLoading ||
    (isFetching && versions.length === 0) ||
    (versions.length > 0 && !selectedVersionId);

  // Re-derive the retained selection's pair once the newly scoped list lands,
  // so the parent diffs against the right predecessor.
  // eslint-disable-next-line no-restricted-syntax -- reason: Need to sync selection state with async data loading
  useEffect(() => {
    if (!needsPairRederive || isFetching || versions.length === 0) {
      return;
    }

    setNeedsPairRederive(false);
    const retained = versions.find(v => v.version === selectedVersionId);
    if (retained) {
      selectVersion(retained);
    } else {
      // Not in the new list — let the auto-select effect below take over.
      setSelectedVersionId(undefined);
    }
  }, [
    needsPairRederive,
    isFetching,
    versions,
    selectedVersionId,
    selectVersion,
  ]);

  // Auto-select first version when fresh data loads and no selection exists
  // eslint-disable-next-line no-restricted-syntax -- reason: Need to sync selection state with async data loading
  useEffect(() => {
    // Wait for fetch to complete before making initial selection
    // This ensures we select from fresh data, not stale cache
    if (versions.length > 0 && !selectedVersionId && !isFetching) {
      const indexToSelect =
        initialSelectionMode === 'previous' && versions.length > 1 ? 1 : 0;
      selectVersion(versions[indexToSelect]);
    }
  }, [
    versions,
    selectedVersionId,
    isFetching,
    selectVersion,
    initialSelectionMode,
  ]);

  return (
    <Flex
      direction="column"
      gap={4}
      width="full"
      height="full"
      data-testid="version-history-panel"
    >
      {/* Header with title and filter */}
      <Flex align="center" justify="between" width="full">
        <Title level="h4" data-testid="version-history-title">
          {t('automation.versionHistory.title')}
        </Title>

        <VersionHistoryStageFilter
          filter={filter}
          onFilterChange={handleFilterChange}
        />
      </Flex>

      {/* Version history list */}
      <VersionHistoryList
        versions={versions}
        selectedVersionId={selectedVersionId}
        onVersionClick={selectVersion}
        isLoading={showLoading}
        isError={isError}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        changes={changes}
        onChangeActivate={onChangeActivate}
        onChangeSelect={onChangeSelect}
      />

      {isRestoreDialogOpen && versionToRestore && (
        <RestoreAlertDialog
          open={isRestoreDialogOpen}
          version={versionToRestore}
          fromPublished={fromPublished ?? false}
          isLoading={isRestoring}
          onOpenChange={open => {
            if (!open && !isRestoring) {
              handleRestoreDialogClose();
            }
          }}
          onConfirm={handleRestoreConfirm}
        />
      )}

      <AutomationOpenDraftDialog
        open={isDraftDialogOpen}
        onClose={closeDraftDialog}
        automationId={automationId}
        onDraftCreate={invalidateRevisionLists}
      />
    </Flex>
  );
}
